/**
 * Chart preview component for the edit page.
 * Reuses ChartContainer to render chart preview with animation enabled.
 * Accepts optional ChartEditorState to apply live visual editor overrides.
 */
import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Chart } from '../../api/types';
import { ChartContainer } from '../charts/shared/ChartContainer';
import { useTheme } from '../../contexts/ThemeContext';
import type { ChartData } from '../charts/echarts/optionBuilders';
import type { ChartEditorState } from './visualConfig';

interface ChartPreviewProps {
  chart: Chart;
  chartData: ChartData | undefined;
  isLoading?: boolean;
  error?: string | null;
  /** Editor state for live preview overrides */
  editorState?: ChartEditorState;
}

const useStyles = makeStyles({
  container: {
    padding: '16px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

const ChartPreview: React.FC<ChartPreviewProps> = ({
  chart,
  chartData,
  isLoading = false,
  error = null,
  editorState,
}) => {
  const { isDarkMode } = useTheme();
  const styles = useStyles();

  // Empty placeholder when no data, not loading, and no error
  if (!chartData && !isLoading && !error) {
    return (
      <div className={styles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: tokens.colorNeutralForeground4,
          fontSize: tokens.fontSizeBase400,
        }}>
          Chart preview will appear here
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ChartContainer
        chartType={chart.chartType || 'line'}
        chartData={chartData}
        chartConfig={chart}
        isDark={isDarkMode}
        isLoading={isLoading}
        error={error}
        animated
        editorState={editorState}
        hideTitle
      />
    </div>
  );
};

export default ChartPreview;